<template>
  <v-card class="white vetalfa-view" :max-width="_getMaxWidth()">
    <div class="vetalfa-header" ref="screenHeader">
      <vetalfa-small-header
        :title="_getLabel('DDX_TITLE')"
      ></vetalfa-small-header>
    </div>
    <div ref="screenContent">
      <v-stepper flat v-model="stepModel" vertical>
        <v-stepper-step step="1" @click="stepModel = 1" class="vetalfa-pointer">
          <span class="vetalfa-tab-text">Informações do Paciente</span>
          <small class="mt-1">Selecione a espécie do animal.</small>
        </v-stepper-step>

        <v-stepper-content step="1" class="px-0 mr-1">
          <vetalfa-section-title
            prop="Selecione a espécie do paciente:"
          ></vetalfa-section-title>
          <v-row class="ma-0 pa-0" justify="center">
            <v-col :cols="!!$vuetify.breakpoint.xsOnly ? 12 : 8">
              <animal-selector
                v-on:selectAnimal="selectAnimal"
                :plural="false"
                :hasBothAnimals="false"
                :defaultValue="null"
              ></animal-selector>
            </v-col>
          </v-row>
          <vetalfa-section-title
            prop="Selecione o sexo do paciente:"
          ></vetalfa-section-title>
          <v-list-item-group
            active-class="algorithm-node-option-active"
            v-model="sex"
          >
            <v-list-item dense :three-line="false">
              <template v-slot:default="{ active }">
                <v-list-item-action class="mr-2 pa-0">
                  <v-checkbox :input-value="active"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title style="white-space: normal !important">
                    Fêmea
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
            <v-list-item dense :three-line="false">
              <template v-slot:default="{ active }">
                <v-list-item-action class="mr-2 pa-0">
                  <v-checkbox :input-value="active"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title style="white-space: normal !important">
                    Macho
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-item-group>
          <vetalfa-section-item
            prop="O sexo do paciente é opcional. Em breve, você poderá escolher mais opções sobre seu paciente."
            class="ma-0 mt-2 pa-0"
          ></vetalfa-section-item>
        </v-stepper-content>

        <v-stepper-step step="2" @click="stepModel = 2" class="vetalfa-pointer">
          <span class="vetalfa-tab-text">Sinais Clínicos</span>
          <small class="mt-1"
            >Selecione no mínimo dois (2) sinais clínicos.</small
          >
        </v-stepper-step>

        <v-stepper-content step="2" class="px-0 mr-1">
          <v-row class="ma-0" justify="end">
            <v-autocomplete
              :loading="!availableSigns.length"
              v-model="inputModel"
              :key="inputModel"
              :search-input.sync="search"
              :items="availableSigns"
              item-text="sign"
              return-object
              :placeholder="'Busque o sinal clínico desejado'"
              :menu-props="{ maxHeight: 200 }"
              persistent-hint
              hide-details
              class="px-2 pa-0"
              :no-data-text="_getLabel('SIGN_NO_DATA')"
              item-color="primary"
              @change="search = ''"
              clearable
              outlined
              :filter="customFilter"
              dense
            >
              <template v-slot:item="{ item }">
                <p
                  class="ma-0 pa-0 my-2 vetalfa-autocomplete-component-size v-list-item__content v-list-item__title"
                >
                  {{ item.sign.trim() }}
                </p>
              </template>
            </v-autocomplete>
          </v-row>
          <v-row v-if="sortedSigns.length === 0" class="ma-0 pa-0 py-5">
            <v-col cols="12" justify="center" class="ma-0 pa-0 pt-2 mt-5">
              <v-row justify="center" class="ma-0 pa-0">
                <v-img
                  position="center"
                  height="50px"
                  class="ma-0 pa-0"
                  :src="noResultsImage"
                  :lazy-src="noResultsImage"
                  max-width="150"
                  contain
                ></v-img>
              </v-row>
            </v-col>
            <v-col cols="12" justify="center" class="ma-0 pa-0 mt-2">
              <p
                class="ma-0 pa-0 text-center vetalfa-card-title-disabled"
                style="font-size: 16px"
              >
                Sem sinais clínicos
              </p>
            </v-col>
            <v-col cols="12" justify="center" class="ma-0 px-2 pa-0 mb-5">
              <v-row justify="center" class="ma-0 pa-0">
                <p class="ma-0 pa-0 text-center vetalfa-card-text-disabled">
                  Quando você adicionar, os sinais selecionados vão aparecer
                  aqui.
                </p>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-else class="ma-0 pa-0 px-2 pb-5 my-5">
            <v-chip-group column>
              <v-chip
                outlined
                v-for="item in sortedSigns"
                :key="item.id"
                @click="removeSign(item)"
                @click:close="removeSign(item)"
                close
                color="primary"
                class="vetalfa-chip-component-size py-1 text-left px-3"
              >
                {{ item.sign }}
              </v-chip>
            </v-chip-group>
          </v-row>
          <v-row
            v-if="sortedSigns.length !== 0"
            class="ma-0 pa-0 my-5"
            justify="end"
          >
            <v-btn
              text
              @click="clearSigns()"
              color="primary"
              class="text-none text-decoration-underline mb-5"
            >
              <v-icon right color="primary" class="mr-2 pb-1">
                {{ mdiTrashCanOutline }}
              </v-icon>
              Limpar sinais
            </v-btn>
          </v-row>
        </v-stepper-content>

        <v-stepper-step
          step="3"
          @click="postSignsAsync"
          class="vetalfa-pointer"
        >
          <span class="vetalfa-tab-text">Condições compatíveis</span>
          <small class="mt-1">
            Resultados correlacionados a todos os dados informados.
          </small>
        </v-stepper-step>
        <v-stepper-content step="3" class="ma-0 pa-0">
          <vetalfa-section-title
            v-if="!loading && (!diagnoses || !diagnoses.length)"
            prop="Não há resultados disponíveis."
          >
            <vetalfa-section-item
              v-if="!diagnoses || !diagnoses.length"
              prop="<b>Não foram encontradas condições compatíveis</b> para os dados informados nos passos um (1) e dois (2)."
            ></vetalfa-section-item>
            <vetalfa-section-item
              v-if="!diagnoses || !diagnoses.length"
              :index="1"
              prop="Os achados clínicos informados estão corretos?"
            >
              <vetalfa-section-sub-item
                v-if="!diagnoses || !diagnoses.length"
                prop="Tente remover algum sinal clínico inconsistente e repita o processo novamente."
              ></vetalfa-section-sub-item>
            </vetalfa-section-item>
          </vetalfa-section-title>
          <vetalfa-section-title
            v-if="!!diagnoses && !!diagnoses.length"
            prop="Condições Compatíveis"
          >
          </vetalfa-section-title>
          <v-row
            v-if="!!diagnoses && !!diagnoses.length"
            class="ma-0 px-4 py-2"
          >
            <v-text-field
              outlined
              dense
              clearable
              :value="searchCondition"
              :disabled="loading"
              :loading="loading"
              hide-details
              v-model="searchCondition"
              :placeholder="_getLabel('SEARCH_TEXT')"
              :prepend-inner-icon="mdiMagnify"
            >
            </v-text-field>
          </v-row>
          <v-row
            v-if="!!filteredDiagnoses && !!filteredDiagnoses.length"
            class="ma-0 pa-0"
          >
            <v-card
              flat
              v-for="diagnosis in filteredDiagnoses"
              :key="`${diagnosis.id} ${diagnosis.diagnosis}`"
              class="vetalfa-pointer vetalfa-card-grey-border pa-2 ma-2 mx-4"
              width="100%"
              height="60px"
            >
              <v-row
                v-if="diagnosis"
                @click="getDiagnosisAsync(diagnosis)"
                aling="center"
                class="ma-0 pa-0 vetalfa-row"
              >
                <v-col class="ma-0 pa-0 vetalfa-card-text" cols="11">
                  <v-row class="ma-0 pa-0 vetalfa-row text-left" align="center">
                    {{ diagnosis.diagnosis }}
                  </v-row>
                </v-col>
                <v-col class="ma-0 pa-0" cols="1">
                  <v-row
                    justify="end"
                    align="center"
                    class="ma-0 pa-0 vetalfa-row"
                  >
                    <v-icon x-small color="#747678">{{
                      mdiChevronRight
                    }}</v-icon>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-row>
          <vetalfa-dialog
            v-on:dialogClose="diagnosisModel = false"
            :title="_getLabel('DDX_TITLE')"
            :toggleModel="diagnosisModel"
          >
            <template v-slot:header>
              <vetalfa-drugs-header
                v-if="diagnosisObject"
                :isDrugsMode="true"
                :name="diagnosisObject.diagnosis"
                :description="diagnosisObject.description"
              ></vetalfa-drugs-header>
            </template>
            <template>
              <div v-if="diagnosisObject">
                <vetalfa-section-title
                  prop="Animal selecionado"
                ></vetalfa-section-title>
                <v-row class="ma-0 pa-0 mx-4" align="end" justify="start">
                  <div cols="2" class="ma-0 pa-0">
                    <v-img
                      max-width="30px"
                      :src="animals.getAnimalIcon(species.value + 1)"
                    ></v-img>
                    <p class="product-animal-name ma-0 pa-0 text-center">
                      {{ animals.getAnimalDescription(species.value + 1) }}
                    </p>
                  </div>
                </v-row>
                <vetalfa-section-title prop="Sinais clínicos preenchidos">
                  <v-chip-group column class="px-4">
                    <v-chip
                      outlined
                      v-for="(item, index) in listOfSigns"
                      :key="index + item.sign"
                      color="primary"
                      class="vetalfa-chip-component-size py-1 text-left px-3"
                    >
                      {{ item.sign }}
                    </v-chip>
                  </v-chip-group>
                </vetalfa-section-title>
                <vetalfa-sections
                  :sections="diagnosisObject.sections"
                ></vetalfa-sections>
                <vetalfa-section-title
                  collapse
                  :isCollapsed="false"
                  prop="Sinais clínicos não preenchidos"
                >
                  <v-chip-group column class="px-4">
                    <v-chip
                      outlined
                      v-for="(item, index) in listOfSignsNotInformed"
                      :key="index + item.sign"
                      color="primary"
                      class="vetalfa-chip-component-size py-1 text-left px-3"
                    >
                      {{ item.sign }}
                    </v-chip>
                  </v-chip-group>
                </vetalfa-section-title>

                <vetalfa-section-title
                  v-if="
                    diagnosisObject.references &&
                    diagnosisObject.references.length
                  "
                  :prop="_getLabel('REFERENCES')"
                  collapse
                  :isCollapsed="false"
                >
                  <div
                    v-for="reference in diagnosisObject.references"
                    :key="reference.name"
                  >
                    <vetalfa-reference-card
                      :reference="reference"
                    ></vetalfa-reference-card>
                  </div>
                </vetalfa-section-title>
                <div style="height: 50px; background-color: white"></div>
              </div>
            </template>
          </vetalfa-dialog>
          <vetalfa-section-item
            prop="Lembre-se, só serão exibidas condições que tenham algum tipo correlação com todos os dados informados nos passos anteriores."
          ></vetalfa-section-item>
          <v-alert
            dense
            v-show="!loading"
            type="info"
            color="primary"
            @click="suggestNewContent"
            class="mt-5 mx-4 px-1 py-2 vetalfa-pointer"
            outlined
          >
            <p class="ma-0 pa-0 vetalfa-subtitle primary--text">
              Não encontrou um resultado esperado?
            </p>
            <p class="ma-0 pa-0 mt-1 vetalfa-subtitle primary--text">
              <span class="text-decoration-underline">Clique aqui</span>
              para sugerir mudanças ou tirar dúvidas.
            </p>
          </v-alert>
        </v-stepper-content>
      </v-stepper>
      <div style="height: 70px; background-color: white"></div>
      <v-row
        class="ma-0 pa-0 white vetalfa-max-width vetalfa-footer"
        align="center"
        justify="space-around"
      >
        <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
          <v-row
            class="ma-0 pa-0 pl-2 pr-1 pb-2"
            align="center"
            justify="start"
          >
            <v-btn
              v-show="!loading && stepModel != 1"
              block
              outlined
              class="text-none primary--text"
              @click="stepModel--"
            >
              Voltar
            </v-btn>
          </v-row>
        </v-col>
        <v-spacer></v-spacer>
        <v-col :cols="$vuetify.breakpoint.xsOnly ? 6 : 4" class="ma-0 pa-0">
          <v-row class="ma-0 pa-0 pr-2 pl-1 pb-2" align="center" justify="end">
            <v-btn
              block
              v-show="loading || stepModel < 4"
              :elevation="5"
              class="text-none vetalfa-primary-btn"
              @click="incrementStepModel()"
              light
            >
              {{ stepModel == 2 ? "Ver resultados" : "Continuar" }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </div>
    <vetalfa-user-section></vetalfa-user-section>
  </v-card>
</template>

<script>
import {
  mdiChevronRight,
  mdiTrashCanOutline,
  mdiMenuDown,
  mdiMagnify,
} from "@mdi/js";
import imagesConstants from "@/constants/images.js";
import { mapGetters, mapActions, mapMutations } from "vuex";
import ReferencesDialog from "@/components/ReferencesDialog.vue";
import AnimalSelector from "@/components/AnimalSelector.vue";
import UserSection from "@/components/UserSection.vue";
import animals from "@/constants/animals.enum.js";
export default {
  name: "Ddx",
  mounted() {
    this.getSigns();
    if (!this.disclaimer) {
      this.setDisclaimer(true);
      this.setConfirmModel({
        active: true,
        alert: true,
        text: this._getLabel("DISCLAIMER"),
        confirmText: this._getLabel("CONTINUE"),
      });
    }
    this.$nextTick(() => {
      this._setupScreenSizes(this);
    });
  },
  components: {
    "vetalfa-references-dialog": ReferencesDialog,
    "animal-selector": AnimalSelector,
    "vetalfa-user-section": UserSection,
  },
  methods: {
    ...mapActions("ddx", ["getSigns", "postSigns", "getDiagnosis"]),
    ...mapActions("general", ["showAlert", "logEvent"]),
    ...mapMutations("general", [
      "setUserSection",
      "setDisclaimer",
      "setConfirmModel",
    ]),
    ...mapMutations("topics", ["setClickedReferences"]),
    async incrementStepModel() {
      if (this.stepModel == 2) {
        return await this.postSignsAsync();
      }
      this.stepModel++;
    },
    suggestNewContent() {
      this.logEvent("suggest_content_click");
      this.setUserSection({ comunication: true });
    },
    selectAnimal(val) {
      this.species = val;
    },
    openReferences(references) {
      this.setClickedReferences(references);
    },
    clearSigns() {
      this.listOfSigns = [];
      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
    removeSign(sign) {
      this.listOfSigns = this.listOfSigns.filter((s) => {
        return s.id !== sign.id;
      });
      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
    formatSentences({ description, eDescription }) {
      let sentences = [];
      if (!!description) {
        sentences = sentences.concat(
          description.replace(/([.?!])\s*(?=[A-Z])/g, "$1|").split("|")
        );
      }
      if (!!eDescription) {
        sentences = sentences.concat(
          eDescription.replace(/([.?!])\s*(?=[A-Z])/g, "$1|").split("|")
        );
      }
      return sentences;
    },
    customFilter(_item, queryText, itemText) {
      itemText = this._createIdentifier(itemText);
      queryText = this._createIdentifier(queryText);
      return itemText.includes(queryText);
    },
    async postSignsAsync() {
      let response = true;
      if (this.hasSignsSpeciesChange) {
        response = await this.postSigns({
          signs: this.listOfIdsSigns,
          species: !!this.species ? { 0: 1, 1: 2 }[this.species.value] : null,
          sex: typeof this.sex === "number" ? this.sex : null,
        });
        this.hasSignsSpeciesChange = !response;
      }
      if (!!response) {
        this.stepModel = 3;
      }
    },
    async getDiagnosisAsync(diagnosis) {
      const response = await this.getDiagnosis({
        id: diagnosis.id,
        diagnosis: diagnosis,
      });
      if (!!response) {
        this.diagnosisModel = true;
        this.diagnosisObject = response;
      }
    },
    selectSign(val) {
      if (this.listOfSigns.length >= 15) {
        return this.showAlert({
          text: this._getMessage("DDX_SIGNS_MAX"),
          color: "black",
        });
      }
      this.listOfSigns.push(val);
      this.$nextTick(() => {
        this.inputModel = null;
        this.search = "";
      });
    },
  },
  computed: {
    ...mapGetters("ddx", ["signs", "diagnoses"]),
    ...mapGetters("general", ["loading", "disclaimer"]),
    availableSigns() {
      let arr = [];
      let group = "";
      if (!!this.signs) {
        this.signs.forEach((s) => {
          if (
            !this.listOfSigns.find((sign) => {
              return sign.id == s.id;
            }) &&
            (typeof this.sex !== "number" ||
              s[{ 0: "female", 1: "male" }[this.sex]] == 1)
          ) {
            if (group != s.group) {
              group = s.group;
              arr.push({ header: s.group });
            }
            arr.push(s);
          }
        });
      }
      return arr;
    },
    filteredDiagnoses() {
      if (!this.searchCondition) {
        return this.diagnoses;
      }
      let arr = [];
      let search = this._removeAccents(
        this.searchCondition.toLowerCase()
      ).split(" ");
      this.diagnoses.forEach((d) => {
        let counter = 0;
        search.forEach((s) => {
          if (this._removeAccents(d.diagnosis.toLowerCase()).includes(s)) {
            counter++;
          }
        });
        if (counter == search.length) {
          arr.push(d);
        }
      });
      return arr;
    },
    sortedSigns() {
      return this.listOfSigns.sort((a, b) =>
        this._removeAccents(a.sign) > this._removeAccents(b.sign) ? 1 : -1
      );
    },
    listOfIdsSigns() {
      let arr = [];
      this.listOfSigns.forEach((s) => {
        arr.push(s.id);
      });
      return arr;
    },
    listOfSignsNotInformed() {
      let arr = [];
      if (!!this.diagnosisObject) {
        this.diagnosisObject.signs.forEach((s) => {
          if (
            !this.listOfSigns.find((sign) => {
              return sign.id == s;
            })
          ) {
            arr = arr.concat(
              this.signs.filter((sign) => {
                return sign.id == s;
              })
            );
          }
        });
      }
      return arr.sort((a, b) =>
        this._removeAccents(a.sign) > this._removeAccents(b.sign) ? 1 : -1
      );
    },
  },
  watch: {
    inputModel(val) {
      if (!!val) {
        this.selectSign(val);
      }
    },
    species() {
      this.hasSignsSpeciesChange = true;
    },
    listOfSigns() {
      this.hasSignsSpeciesChange = true;
    },
    sex() {
      this.hasSignsSpeciesChange = true;
    },
    searchCondition(val) {},
  },
  data() {
    return {
      searchCondition: null,
      animals,
      species: null,
      inputModel: null,
      mdiChevronRight,
      listOfSigns: [],
      interactions: [],
      diagnosisModel: false,
      diagnosisObject: null,
      search: "",
      hasSignsSpeciesChange: false,
      stepModel: 1,
      mdiTrashCanOutline,
      noResultsImage: imagesConstants.NO_RESULTS,
      mdiMenuDown,
      mdiMagnify,
      sex: null,
    };
  },
};
</script>